/**
 * Query Parameters Builder Utility
 *
 * Provides utilities for building URLSearchParams with consistent patterns
 * used across API service methods (pagination, fields, q[] filters).
 */

/**
 * Common pagination and filter parameters used by most API endpoints
 */
export type BaseQueryParams<TFilters = Record<string, unknown>> = {
  page?: number
  limit?: number
  per_page?: number
  fields?: string
  q?: TFilters
  /** Sort parameters - each string represents a sort directive (e.g., "created_at desc") */
  s?: string[]
};

/**
 * Options for building search params
 */
export type BuildSearchParamsOptions = {
  /** Use 'per_page' instead of 'limit' for the page size parameter */
  usePerPage?: boolean
  /** Default values to apply when params are not provided */
  defaults?: {
    limit?: number
    page?: number
  }
};

/**
 * Builds URLSearchParams from common API query parameters.
 *
 * Handles:
 * - page, limit (or per_page), fields as top-level params
 * - q[key] filter notation for nested query filters
 * - Array values expanded as multiple q[key] entries
 * - Null/undefined/empty string values are filtered out
 *
 * @example
 * ```typescript
 * const params = buildSearchParams({
 *   page: 1,
 *   limit: 20,
 *   fields: "id,name,email",
 *   q: {
 *     status_eq: "active",
 *     id_in: ["1", "2", "3"]
 *   }
 * });
 * // Result: "page=1&limit=20&fields=id,name,email&q[status_eq]=active&q[id_in]=1&q[id_in]=2&q[id_in]=3"
 * ```
 */
export function buildSearchParams<TFilters = Record<string, unknown>>(
  params: BaseQueryParams<TFilters> = {},
  options: BuildSearchParamsOptions = {},
): URLSearchParams {
  const searchParams = new URLSearchParams();
  const { usePerPage = false, defaults } = options;

  // Apply defaults
  const page = params.page ?? defaults?.page;
  const limit = params.limit ?? params.per_page ?? defaults?.limit;

  // Add pagination parameters
  if (page !== undefined) {
    searchParams.append("page", page.toString());
  }

  if (limit !== undefined) {
    const limitKey = usePerPage ? "per_page" : "limit";
    searchParams.append(limitKey, limit.toString());
  }

  // Add fields parameter
  if (params.fields) {
    searchParams.append("fields", params.fields);
  }

  // Add q[] filter parameters
  if (params.q) {
    appendFilterParams(searchParams, params.q);
  }

  // Add sort parameters
  if (params.s?.length) {
    params.s.forEach((sortValue) => {
      searchParams.append("s", sortValue);
    });
  }

  return searchParams;
}

/**
 * Appends filter parameters with q[] bracket notation to URLSearchParams.
 *
 * @param searchParams - URLSearchParams instance to append to
 * @param filters - Object containing filter key-value pairs
 */
export function appendFilterParams(
  searchParams: URLSearchParams,
  filters: Record<string, unknown>,
): void {
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }

    if (Array.isArray(value)) {
      // Use [] suffix for array values to produce q[key][]=value format
      value.forEach((v) => {
        if (v !== undefined && v !== null && v !== "") {
          searchParams.append(`q[${key}][]`, String(v));
        }
      });
    }
    else {
      searchParams.append(`q[${key}]`, String(value));
    }
  });
}

/**
 * Builds a complete URL string with query parameters.
 *
 * @param baseUrl - The base URL (endpoint path)
 * @param params - Query parameters to append
 * @param options - Options for building the params
 * @returns Full URL string with query parameters (or just baseUrl if no params)
 *
 * @example
 * ```typescript
 * const url = buildUrlWithParams("/employees", { page: 1, limit: 20 });
 * // Result: "/employees?page=1&limit=20"
 * ```
 */
export function buildUrlWithParams<TFilters = Record<string, unknown>>(
  baseUrl: string,
  params: BaseQueryParams<TFilters> = {},
  options: BuildSearchParamsOptions = {},
): string {
  const searchParams = buildSearchParams(params, options);
  const queryString = searchParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

/**
 * Parses a Ransack query string back into a flat filter object, stripping the
 * `q[...]` wrapper from each key. Inverse of the `q[key]` notation produced by
 * {@link appendFilterParams}: feeds a service's `q` param from a context-built
 * queryString without each caller re-implementing the same regex.
 *
 * @example
 * ```typescript
 * parseRansackQueryString("q[title_cont]=foo&q[erased_eq]=false");
 * // Result: { title_cont: "foo", erased_eq: "false" }
 * ```
 */
export function parseRansackQueryString(queryString: string): Record<string, string> {
  const filters: Record<string, string> = {};
  new URLSearchParams(queryString).forEach((value, key) => {
    const inner = key.match(/^q\[(.+)\]$/)?.[1];
    if (inner) filters[inner] = value;
  });
  return filters;
}

/**
 * Converts a simple object to URLSearchParams with optional prefix.
 * Useful for single-level parameter objects or when q[] notation is not needed.
 *
 * @param obj - Object to convert
 * @param prefix - Optional prefix to add to each key (e.g., "q" for q[key] notation)
 */
export function objectToSearchParams(
  obj: Record<string, unknown>,
  prefix?: string,
): URLSearchParams {
  const searchParams = new URLSearchParams();

  Object.entries(obj).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }

    const paramKey = prefix ? `${prefix}[${key}]` : key;

    if (Array.isArray(value)) {
      // Use [] suffix for array values
      value.forEach((v) => {
        if (v !== undefined && v !== null && v !== "") {
          searchParams.append(`${paramKey}[]`, String(v));
        }
      });
    }
    else {
      searchParams.append(paramKey, String(value));
    }
  });

  return searchParams;
}
